import type { ComponentProps } from 'react';

import { Tooltip } from '@/lib/base/tooltip/tooltip';
import { cx } from '@/utils/cx';

import { useIsTruncated } from './useIsTruncated';

type TooltipPlacement = ComponentProps<typeof Tooltip>['placement'];

interface TruncatedTextProps {
  /** Full text to render; also the tooltip content shown when it is clipped. */
  text: string;
  /** Classes for the single-line, truncating text element. */
  className?: string;
  /** Tooltip placement. @default 'top' */
  placement?: TooltipPlacement;
  /**
   * Classes for the wrapper element that slots into the parent layout.
   * Defaults to a shrinkable block so the inner text can truncate within the
   * available width. Override when the trigger must be a flex item, etc.
   */
  wrapperClassName?: string;
  /**
   * Overrides the text element's accessible name. Use when `text` alone
   * doesn't convey enough context to a screen reader (e.g. a struck-through
   * previous value, where sighted users get the strikethrough styling but a
   * screen reader needs "Previously named X" spelled out).
   */
  'aria-label'?: string;
  /**
   * Whether the text element becomes keyboard-focusable when it is clipped (so
   * keyboard users can reveal the tooltip). @default true
   *
   * Set to `false` when this text is nested inside an already-focusable
   * control (e.g. a `role="button"` rename trigger): a focusable truncated span
   * inside it would add a second, redundant tab stop. The tooltip remains
   * available on pointer hover; the enclosing control carries the accessible
   * name.
   */
  focusable?: boolean;
}

/**
 * A single line of text that truncates with an ellipsis and reveals the full
 * value in a tooltip ONLY when it is actually clipped.
 *
 * Truncation is detected by comparing the element's `scrollWidth` to its
 * `clientWidth`, kept in sync with a `ResizeObserver`, so the tooltip
 * appears/disappears as the container resizes or the text changes. When the
 * text fits, the tooltip is disabled and never shows.
 *
 * The wrapper must live in a width-constrained context (e.g. a `min-w-0` flex
 * parent) for truncation to engage, exactly like a bare `truncate` span.
 */
export function TruncatedText({
  text,
  className,
  placement = 'top',
  wrapperClassName = 'block min-w-0 max-w-full',
  'aria-label': ariaLabel,
  focusable = true,
}: TruncatedTextProps) {
  const { ref: textRef, isTruncated } = useIsTruncated<HTMLSpanElement>([text]);

  return (
    <Tooltip
      title={text}
      placement={placement}
      isDisabled={!isTruncated}
      interactive
      wrapperClassName={wrapperClassName}
    >
      <span
        ref={textRef}
        className={cx('block truncate', className)}
        tabIndex={focusable && isTruncated ? 0 : undefined}
        aria-label={ariaLabel}
      >
        {text}
      </span>
    </Tooltip>
  );
}

export default TruncatedText;
